import { getToken } from "shared/utils/getToken";
import { apiSlice } from "../";
import { EditionForm } from "shared/interfaces";

export interface Edicao {
  slice(arg0: number): unknown;
  idEdicao: number;
  nome: string;
  notaCorte: number;
  dataAberturaInscricao: string;
  dataFimInscricao: string;
  inscricoesAberta: "T" | "F";
  etapas: Etapa[];
  aberturaAgendada: "T" | "F";
}

export interface Etapa {
  idEtapa: number;
  nome: string;
  peso: number;
  ordem: number;
}

export interface UpdateEtapasEdicao {
  idEtapa: number;
  peso: number;
  ordem: number;
}

export interface UpdateEtapasEdicaoRequest {
  idEdicao: number;
  etapas: UpdateEtapasEdicao[];
}

export interface EdicaoUpdate {
  nome: string | null;
  notaCorte: number | null;
  dataAberturaInscricao: string | null;
  dataFimInscricao: string | null;
  aberturaAgendada: string | null;
}

export interface UpdateEdicaoRequest {
  idEdicao: number;
  edicao: EdicaoUpdate;
}

export type Lista = Edicao[];

const edicaoSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getEdicaoAtual: builder.query<Edicao, void>({
      query: () => ({
        url: "edicao/edicao-atual",
        method: "GET",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
      providesTags: ["Edicao"],
    }),
    getEdicaoById: builder.query<Edicao, number>({
      query: (id) => ({
        url: `edicao/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
      providesTags: (result, error, id) => [{ type: 'Edicao', id }]
    }),
    getListaEdicoes: builder.query<Edicao[], boolean | void>({
      query: (ativo = true) => ({
        url: "edicao",
        method: "GET",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        params: {
          ativo: ativo
        }
      }),
      providesTags: ["Edicao"],
    }),
    postNewEdicao: builder.mutation<void, EditionForm>({
      query: (data) => ({
        url: "edicao",
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: {
          nome: data.nome,
          notaCorte: data.notaCorte,
          dataAberturaInscricao: data.dataAberturaInscricao,
          dataFimInscricao: data.dataFimInscricao,
        },
      }),
      invalidatesTags: ["Edicao"],
    }),
    deleteEdicao: builder.mutation<void, number>({
      query: (id) => ({
        url: `edicao/${id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
      invalidatesTags: ["Edicao"]
    }),
    reativarEdicao: builder.mutation<void, number>({
      query: (id) => ({
        url: `edicao/${id}/reativar`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
      invalidatesTags: ["Edicao"]
    }),
    updateEtapasEdicao: builder.mutation<void, UpdateEtapasEdicaoRequest>({
      query: (data) => ({
        url: `edicao/${data.idEdicao}/etapas`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: {
          etapas: data.etapas,
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Edicao' as const, id: arg.idEdicao }
      ]
    }),
    updateEdicao: builder.mutation<void, UpdateEdicaoRequest>({
      query: (data) => ({
        url: `edicao/${data.idEdicao}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: {
          nome: data.edicao.nome,
          notaCorte: data.edicao.notaCorte,
          dataAberturaInscricao: data.edicao.dataAberturaInscricao,
          dataFimInscricao: data.edicao.dataFimInscricao,
          aberturaAgendada: data.edicao.aberturaAgendada
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Edicao' as const, id: arg.idEdicao }
      ]
    }),
    updateInscricoesAberta: builder.mutation<
      void,
      { idEdicao: number; abrir: boolean }
    >({
      query: (data) => ({
        url: `edicao/${data.idEdicao}/inscricoes?abrir=${data.abrir}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetEdicaoAtualQuery,
  useGetEdicaoByIdQuery,
  useUpdateEtapasEdicaoMutation,
  useUpdateEdicaoMutation,
  usePostNewEdicaoMutation,
  useDeleteEdicaoMutation,
  useGetListaEdicoesQuery,
  useUpdateInscricoesAbertaMutation,
  useReativarEdicaoMutation,
} = edicaoSlice;
